/**
 * @file edit-warehouse.tsx
 * @module app/modules/warehouse/edit-warehouse
 * @description Warehouse editing page in UTrade Admin.
 * Loads existing warehouse data, allows detail updates, and persists changes with audit logging.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.2.1
 */

import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, InputGroup, Row } from 'react-bootstrap';
import { CustomSelectTable2 } from '../../custom/Select/custom-select-table';
import clsx from 'clsx';
import AutoComplete from '../../custom/autoComplete';
import { PermissionModule } from '../../../utils/constants';
import Validations from '../../../utils/validations';
import APICallService from '../../../api/apiCallService';
import { multipleWarehouse } from '../../../api/apiEndPoints';
import { useLocation, useNavigate } from 'react-router-dom';
import Loader from '../../../Global/loader';
import { success } from '../../../Global/toast';
import { warehouse as warehouseStoreString } from '../../../utils/storeString';
import { getKey } from '../../../Global/history';
import Method from '../../../utils/methods';
import { useAuth } from '../auth';
import { data } from '../../custom/charts/business-types-chart';

const EditWarehouse = () => {
  const { currentUser } = useAuth();
  let tempWarehouse: any = getKey(warehouseStoreString.warehouseRef);
  tempWarehouse = JSON.parse(tempWarehouse);
  const navigate = useNavigate();
  const { state }: any = useLocation();
  const [fetchLoading, setFetchLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [oldwarehouseData, setOldWarehouseData] = useState<any>({
    name: '',
    phone: '',
    address: '',
    lat: '',
    lng: '',
    email: '',
  });
  const [warehouseData, setWarehouseData] = useState<any>({
    name: '',
    phone: '',
    address: '',
    lat: '',
    lng: '',
    email: '',
    districts: [],
    city: [],
    managers: [],
    loadingArea: [],
  });
  const [validations, setValidations] = useState<any>({
    name: false,
    phone: false,
    address: false,
    email: false,
    districts: false,
    managers: false,
    loadingArea: false,
  });

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    (async () => {
      if (!state) {
        return window.history.back();
      }
      await fetchWarehouse();
    })();
  }, []);

  const fetchCitys = (district: any) => {
    let cities: any = [];
    district?.map((val: any) => {
      let tempCity = cities?.find(
        (item: any) => item?.value == val?.city?.reference
      );
      if (!tempCity && val.city) {
        cities?.push({
          value: val?.city?.reference,
          label: val?.city?.name,
          title: val?.city?.name,
        });
      }
    });
    return cities;
  };
  const fetchWarehouse = async () => {
    setFetchLoading(true);
    const apiService = new APICallService(
      multipleWarehouse.warehouseInfo,
      state?.id,
      '',
      '',
      '',
      '',
      PermissionModule.WarehouseManger,
      tempWarehouse?._id
    );
    const response = await apiService.callAPI();
    let data: any = [];
    if (response) {
      // setWarehouseList(response.records);
      setWarehouseData({
        name: response.name || '',
        phone: response.phone || '',
        address: response.address || '',
        lat: response.lat || '',
        long: response.lng || '',
        email: response?.email || '',
        city: fetchCitys(response?.district),
        districts: response?.district.length
          ? response?.district.map((val: any) => {
              return {
                value: val.reference,
                label: val.name,
                title: val.name,
              };
            })
          : [],
        managers: response.manager.length
          ? response?.manager.map((val: any) => {
              return {
                value: val.reference,
                label: val.name,
                title: val.name,
              };
            })
          : [],
      });
      setOldWarehouseData({
        name: response.name || '',
        phone: response.phone || '',
        address: response.address || '',
        lat: response.lat || '',
        long: response.lng || '',
        email: response?.email || '',
      });
    }
    setFetchLoading(false);
  };
  const handleChange = (value: any, name: keyof any) => {
    const temp = { ...warehouseData };
    const tempValidation: any = { ...validations };
    temp[name] = value;
    tempValidation[name] = value.trim().length === 0;
    setValidations(tempValidation);
    setWarehouseData(temp);
  };
  const handleManagerChange = (data: any) => {
    const tempValidation = { ...validations };
    setWarehouseData((prevData: any) => ({
      ...prevData,
      managers: data,
    }));
    if (data.length) {
      tempValidation.managers = false;
    } else {
      tempValidation.managers = true;
    }
    setValidations(tempValidation);
  };
  const handleDistrictChange = (data: any) => {
    const tempValidation = { ...validations };
    setWarehouseData((prevData: any) => ({
      ...prevData,
      districts: data,
    }));
    if (data.length) {
      tempValidation.districts = false;
    } else {
      tempValidation.districts = true;
    }
    setValidations(tempValidation);
  };

  const handleAddressChange = async (value: string, lat: any, lng: any) => {
    const normalizedAddress = value.trimStart();
    setWarehouseData((prev: any) => ({
      ...prev,
      address: normalizedAddress,
      lat,
      lng,
      long: lng,
    }));

    setValidations((prev: any) => ({
      ...prev,
      address: !normalizedAddress.length,
    }));
  };

  const handleSave = async () => {
    const tempValidation = { ...validations };
    tempValidation.name = warehouseData.name.trim().length === 0;
    tempValidation.phone = warehouseData.phone.trim().length === 0;
    tempValidation.address = warehouseData.address.trim().length === 0;
    tempValidation.email = warehouseData.email.trim().length === 0;
    const isValid = await Validations.validateObject(tempValidation);
    if (isValid) {
      setLoading(true);
      const params = {
        name: warehouseData.name,
        phone: warehouseData.phone,
        phoneCountry: '+255',
        address: warehouseData.address,
        lng: warehouseData.long,
        lat: warehouseData.lat,
        email: warehouseData.email,
      };
      const apiService = new APICallService(
        multipleWarehouse.updateWarehouse,
        params,
        { id: state?.id },
        '',
        '',
        '',
        PermissionModule.WarehouseManger,
        tempWarehouse?._id
      );
      const response = await apiService.callAPI();
      if (response) {
        success('Warehouse updated successfully!');
        navigate('/warehouse/all-warehouse');
        const { loadingArea, managers, city, districts, ...restData } =
          warehouseData;

        const changes = Method.compareComplexObjects(
          oldwarehouseData,
          restData
        );
        if (changes.newValues.length || changes.oldValues.length) {
          
        }
      }
      setLoading(false);
    }
    setValidations(tempValidation);
  };

  return (
    <div className="p-5">
      <Row className="align-items-center">
        <Col xs={12} className="mb-5">
          <h1 className="fs-22 fw-bolder">Edit warehouse</h1>
        </Col>
        {!fetchLoading ? (
          <>
            <Col xs={12}>
              <Card className="bg-light border mb-7">
                <Card.Body className="px-7">
                  <Row className="gx-lg-9 gx-6 gy-6">
                    <Col md={6}>
                      <Row className="gy-2 align-items-center">
                        <Col sm={4}>
                          <label htmlFor="" className="fs-16 fw-500">
                            Name
                          </label>
                        </Col>
                        <Col sm={8}>
                          <Form.Control
                            className={clsx(
                              'form-control-custom bg-white h-60px',
                              {
                                'border-danger': validations.name,
                              }
                            )}
                            type="text"
                            placeholder="Type here..."
                            name="name"
                            value={warehouseData.name}
                            onChange={(e: any) =>
                              handleChange(e.target.value, 'name')
                            }
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col md={6}>
                      <Row className="gy-2 align-items-center">
                        <Col sm={4}>
                          <label htmlFor="" className="fs-16 fw-500">
                            Warehouse Contact number
                          </label>
                        </Col>
                        <Col sm={8}>
                          <InputGroup
                            size="lg"
                            className={`${
                              validations.phone
                                ? 'border border-1 border-danger rounded-3'
                                : ''
                            }`}
                          >
                            <InputGroup.Text id="inputGroup-sizing-lg">
                              +255
                            </InputGroup.Text>
                            <Form.Control
                              className={clsx(
                                'form-control-custom bg-white h-60px'
                              )}
                              type="number"
                              placeholder="Type here…"
                              name="phone"
                              value={warehouseData.phone}
                              onChange={(e: any) =>
                                handleChange(e.target.value, 'phone')
                              }
                            />
                          </InputGroup>
                        </Col>
                      </Row>
                    </Col>
                    <Col md={6}>
                      <Row className="gy-2 align-items-center">
                        <Col sm={4}>
                          <label htmlFor="" className="fs-16 fw-500">
                            Address
                          </label>
                        </Col>
                        <Col sm={8}>
                          <AutoComplete
                            address={warehouseData.address}
                            handleAddressChange={handleAddressChange}
                            lat={warehouseData.lat}
                            lng={warehouseData.lng}
                            border={clsx(validations.address ? '#e55451' : '')}
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col md={6}>
                      <Row className="gy-2 align-items-center">
                        <Col sm={4}>
                          <label htmlFor="" className="fs-16 fw-500">
                            Email
                          </label>
                        </Col>
                        <Col sm={8}>
                          <Form.Control
                            className={clsx(
                              'form-control-custom bg-white h-60px',
                              {
                                'border-danger': validations.email,
                              }
                            )}
                            type="text"
                            placeholder="Type here..."
                            name="email"
                            value={warehouseData.email}
                            onChange={(e: any) =>
                              handleChange(e.target.value, 'email')
                            }
                          />
                          {/* {validation.name && (
                        <div className="text-danger fs-12 fw-bold">
                          Please enter a warehouse name
                        </div>
                      )} */}
                        </Col>
                      </Row>
                    </Col>
                    <Col md={12}>
                      <Row className="gy-2 align-items-center">
                        <Col sm={2}>
                          <label htmlFor="" className="fs-16 fw-500">
                            Assign city
                          </label>
                        </Col>
                        <Col sm={10}>
                          <CustomSelectTable2
                            border={clsx(validations.city ? '#e55451' : '')}
                            backgroundColor="white"
                            multiValueBackground="#e7f1fd"
                            indicatorDisplay="none"
                            display="none"
                            isMulti={true}
                            menuIsOpen={false}
                            controlFontSize="1.077rem"
                            minHieight="60px"
                            options={warehouseData.city}
                            value={warehouseData.city}
                            isDisabled={true}
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col md={12}>
                      <Row className="gy-2 align-items-center">
                        <Col sm={2}>
                          <label htmlFor="" className="fs-16 fw-500">
                            Assign districts
                          </label>
                        </Col>
                        <Col sm={10}>
                          <CustomSelectTable2
                            border={clsx(
                              validations.districts ? '#e55451' : ''
                            )}
                            backgroundColor="white"
                            multiValueBackground="#e7f1fd"
                            indicatorDisplay="none"
                            display="none"
                            isMulti={true}
                            menuIsOpen={false}
                            controlFontSize="1.077rem"
                            minHieight="60px"
                            options={warehouseData.districts}
                            value={warehouseData.districts}
                            onChange={handleDistrictChange}
                            isDisabled={true}
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col md={12}>
                      <Row className="gy-2 align-items-center">
                        <Col sm={2}>
                          <label htmlFor="" className="fs-16 fw-500">
                            Assign Managers
                          </label>
                        </Col>
                        <Col sm={10}>
                          <CustomSelectTable2
                            border={clsx(validations.managers ? '#e55451' : '')}
                            backgroundColor="white"
                            multiValueBackground="#e7f1fd"
                            indicatorDisplay="none"
                            display="none"
                            isMulti={true}
                            menuIsOpen={false}
                            controlFontSize="1.077rem"
                            minHieight="60px"
                            options={warehouseData.managers}
                            value={warehouseData.managers}
                            onChange={handleManagerChange}
                            isDisabled={true}
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
            <Col>
              <Row>
                <Col sm={3}>
                  <Button
                    className="uw-btn uw-btn-primary"
                    disabled={loading}
                    onClick={handleSave}
                  >
                    {!loading && (
                      <span className="indicator-label">Update</span>
                    )}
                    {loading && (
                      <span
                        className="indicator-progress"
                        style={{ display: 'block' }}
                      >
                        Please wait...
                        <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                      </span>
                    )}
                  </Button>
                </Col>
              </Row>
            </Col>{' '}
          </>
        ) : (
          <Col>
            <div className="d-flex justify-content-center">
              <Loader loading={fetchLoading} />
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
};
export default EditWarehouse;
